import { Box, Typography, makeStyles } from "@material-ui/core";
import * as React from "react";
import { useHistory } from "react-router";
import { useRecoilValue } from "recoil";

import { CustomButton } from "@/components/Button";
import { CompatibilityRadarInfoButton } from "@/components/compatibility_radar_chart/CompatibilityRadarInfoModal";
import { DiagnosisCompatibilityAgentsStepChips } from "@/components/diagnosis/DiagnosisCompatibilityAgentsStepChips";
import { PlannerListCard } from "@/components/planner_onboarding/PlannerCards";
import { PLANNER_MOTION } from "@/components/planner_onboarding/plannerMotion";
import { PlannerReveal, usePlannerReducedMotion } from "@/components/planner_onboarding/PlannerReveal";
import { RecommendedPlannerCarousel } from "@/components/planner_onboarding/RecommendedPlannerCarousel";
import { LoadingPanel } from "@/components/StatePanels";
import { useCompatibilityAgents } from "@/hooks/useCompatibilityAgents";
import { PlannerIntroductionLayout, plannerIntroductionActionStyles } from "@/layouts/PlannerIntroductionLayout";
import { getInvestmentDiagnosisResult } from "@/logics/investmentDiagnosis";
import theme from "@/theme";
import { hasUsableAgentId } from "@/utilities/agentId";
import { sendEventToAppMeasurementTool, AppEventTokenEnum } from "@/utilities/capacitorLogic";
import { AGENT_DETAIL_URL_SOURCE } from "@/utilities/userOffer";
import { UserState } from "@/view_models/atoms";

import { DiagnosisCompatibilityAgentsEmpty } from "./components/DiagnosisCompatibilityAgentsEmpty";
import { DiagnosisCompatibilityAgentsGoHomeButton } from "./components/DiagnosisCompatibilityAgentsGoHomeButton";
import { DiagnosisCompatibilityAgentsHeading } from "./components/DiagnosisCompatibilityAgentsHeading";
import { usePlannerResultMotion } from "./hooks/usePlannerResultMotion";

const useStyles = makeStyles(() => ({
  gutter: { marginLeft: 24, marginRight: 24 },
  heading: { marginBottom: 32 },
  recommended: { marginBottom: 16 },
  cards: { display: "flex", flexDirection: "column", gap: 12 },
  sectionHeading: { fontSize: 16, fontWeight: 700, lineHeight: 1.5 },
  description: { marginTop: 8, fontSize: 14, lineHeight: 1.5 },
  otherDescription: { marginTop: 8, marginBottom: 16, fontSize: 14, lineHeight: 1.5, color: theme.palette.semantic["text/secondary"] },
  help: { marginTop: 16 },
  otherPlanners: { marginTop: 32 },
  skipAction: { ...plannerIntroductionActionStyles, width: "calc(100% - 48px)" },
  action: {
    position: "fixed",
    left: 0,
    right: 0,
    bottom: 0,
    zIndex: theme.zIndex.appBar,
    padding: "16px 24px calc(24px + env(safe-area-inset-bottom, 0px))",
    backgroundColor: theme.palette.semantic["surface/card"],
    boxShadow: "0 -2px 8px rgba(22,32,46,0.08)",
  },
  button: { maxWidth: 360, margin: "0 auto" },
  actionSpacer: { height: "calc(104px + env(safe-area-inset-bottom, 0px))" },
}));

export const DiagnosisCompatibilityAgentsResultPage: React.FC = () => {
  const history = useHistory();
  const classes = useStyles();
  const user = useRecoilValue(UserState);
  const { agents, isLoading, error } = useCompatibilityAgents(true, { includeAllPublic: true });
  const reduced = usePlannerReducedMotion();
  const { animate, initialCarouselPosition, rememberScroll } = usePlannerResultMotion(user?.id ?? 0, !isLoading && !error && agents.length > 0);
  const railRef = React.useRef<HTMLDivElement>(null);
  const position = React.useRef(initialCarouselPosition);
  // 詳細画面でのマッチ後は一覧再取得で is_matched が反映されるため、画面間の状態受け渡しなしで検知できる
  const hasMatchedAgent = agents.some((agent) => agent.is_matched);
  const isEmpty = !isLoading && !error && agents.length === 0;
  const showHomeButton = !isLoading && !error && hasMatchedAgent;
  const { resultLowComment } = getInvestmentDiagnosisResult(user);
  const showSkipButton = !isLoading && !error && !hasMatchedAgent && resultLowComment !== null;

  const handleGoHome = () => history.push("/mypage");

  const hasSentListAccess = React.useRef(false);
  React.useEffect(() => {
    // 読み込み中・通信エラーは除外し、空状態も画面への到達として数える。
    // 同じ画面内の再描画・再取得では送らない。再訪は Adjust 側でユーザー単位に集計する。
    if (isLoading || error || hasSentListAccess.current) return;
    hasSentListAccess.current = true;
    sendEventToAppMeasurementTool(AppEventTokenEnum.RecommendedPlannerListAccess);
  }, [isLoading, error]);

  React.useEffect(() => {
    if (error) history.replace("/mypage");
  }, [error, history]);

  const handleClickDetail = (agent: typeof agents[number]) => {
    if (!hasUsableAgentId(agent)) return;
    rememberScroll({ ...position.current, scrollLeft: railRef.current?.scrollLeft ?? position.current.scrollLeft });
    // エージェント詳細への遷移イベント発火
    sendEventToAppMeasurementTool(AppEventTokenEnum.RecommendedPlannerDetailTap);
    // 一覧の並び（上位3人がカルーセル、4人目以降がリスト）での順位を詳細に渡し、上位3人の王冠を詳細でも出す
    const rank = agents.findIndex((item) => item.id === agent.id) + 1;
    history.push(`/agent_search/${agent.id}?source=${AGENT_DETAIL_URL_SOURCE.DIAGNOSIS_COMPATIBILITY_AGENTS}`, {
      fromDiagnosisCompatibilityAgents: true,
      compatibilityRank: rank > 0 ? rank : undefined,
    });
  };

  if (error) return null;

  // 要素間の余白（margin）は Page 側の Box で持ち、各コンポーネントは他要素の存在を前提にしない
  return (
    <PlannerIntroductionLayout
      mode={isLoading || error || isEmpty ? "state" : "list"}
      phaseTags={<DiagnosisCompatibilityAgentsStepChips />}
      fullBleed
    >
      <PlannerReveal animate={animate && !reduced && !isLoading} duration={PLANNER_MOTION.reveal} className={`${classes.heading} ${classes.gutter}`}>
        <DiagnosisCompatibilityAgentsHeading />
      </PlannerReveal>
      {isLoading ? (
        <Box className={classes.gutter}><LoadingPanel message="相性のよいプランナーを探しています" /></Box>
      ) : agents.length === 0 ? (
        <Box className={classes.gutter}><DiagnosisCompatibilityAgentsEmpty /></Box>
      ) : (
        <>
          <PlannerReveal delay={PLANNER_MOTION.recommended} duration={PLANNER_MOTION.reveal} animate={animate && !reduced} className={`${classes.gutter} ${classes.recommended}`}>
            <Typography component="h2" className={classes.sectionHeading}>あなたの要望に特化した認定プランナー</Typography>
            <Typography className={classes.description}>診断結果から、特に相性のよい{Math.min(agents.length, 3)}名をピックアップしました。</Typography>
            <Box className={classes.help}><CompatibilityRadarInfoButton variant="compact-row" surface="card" /></Box>
          </PlannerReveal>
          <RecommendedPlannerCarousel agents={agents.slice(0, 3)} animate={animate} reduced={reduced} railRef={railRef} initialPosition={initialCarouselPosition}
            onPositionChange={value => { position.current = value; }} onDetail={handleClickDetail} />
          {agents.length > 3 && (
            <Box className={`${classes.otherPlanners} ${classes.gutter}`}>
              <PlannerReveal onScroll duration={PLANNER_MOTION.scrollReveal} animate={animate && !reduced}>
                <Typography component="h2" className={classes.sectionHeading}>ほかの認定プランナーも見る</Typography>
                <Typography className={classes.otherDescription}>相性スコアの高い順に表示しています。</Typography>
              </PlannerReveal>
              <Box className={classes.cards}>
                {agents.slice(3).map((agent, index) => (
                  <PlannerReveal key={agent.id ?? `other-${index}`} onScroll delay={Math.min(index, 2) * 60} duration={PLANNER_MOTION.scrollReveal} animate={animate && !reduced}>
                    <PlannerListCard agent={agent} rank={index + 4} onDetail={() => handleClickDetail(agent)} />
                  </PlannerReveal>
                ))}
              </Box>
            </Box>
          )}
        </>
      )}
      {showSkipButton && (
        <Box className={classes.skipAction} data-testid="diagnosis-planners-skip-action">
          <CustomButton tone="text" buttonSize="L" fullWidth onClick={handleGoHome}>
            スキップ
          </CustomButton>
        </Box>
      )}
      {showHomeButton && (
        <>
          <Box className={classes.actionSpacer} aria-hidden="true" />
          <Box className={classes.action} data-testid="diagnosis-planners-home-action">
            <Box className={classes.button}>
              <DiagnosisCompatibilityAgentsGoHomeButton onClick={handleGoHome} />
            </Box>
          </Box>
        </>
      )}
    </PlannerIntroductionLayout>
  );
};
